/**
 * @description 所有业务之前的公共逻辑，自动执行, 一个任务一个初始化方法
 * @author librajt, duanjun
 */

import { gdpr } from '@umu/umu-appetize';
import { gdpr as gdprCom } from '@umu/umu-appetize-com';
import * as ua from '@umu/umu-ua';
import { getData } from '@umu/umu-util';
import * as imageApi from '@umu/umu-image-handle';
import { IPageData } from '@umu/umu-types';
import { initBodyClassName, isSystem } from '@umu/ukit-lang';
import { $window, loadScriptAsync, redirectTo, setTitle } from '@umu/umu-dom';
import { getGdprBootJsUrl } from '@umu/umu-mobile-layout';
import { renderFullWatermark } from '@umu/umu-full-watermark';
import { components } from 'common';
const { AlertDialog } = components;
import { getLoginUrl, checkXFrameAllow, isInMiniProgram } from '../@utils/umuUtil';
import userAchievementService from '../components/user/@userAchievement/userAchievementService';

class Page<T extends IPageData> {
    constructor(private data: T) {
        this.init();
    }

    private goLogin() {
        const loginUrl = window.location.host.indexOf('m.') === 0 ? '/globaluser/loginpage' : '/auth/login';
        redirectTo(loginUrl);
    }

    public initGdprCom() {
        if (ua.isUMUWebview) {
            return;
        }
        if (ua.isInIframe()) {
            return;
        }
        const userId = getData(this.data, 'userInfo', 'student_id') || '0';
        const lang = getData(this.data, 'Lang');
        // 不好拿到 IS_PRODUCTION，先 hack 处理一下
        let isProduction = true;
        isProduction = false;
        const gdprUrl = getGdprBootJsUrl(isProduction, this.data.SYSTEM);
        gdprCom({
            userId,
            platform: window.location.host.indexOf('m.') === 0 ? 'm' : 'web',
            lang,
            disableDeclineAlways: this.data.gdprConfig && this.data.gdprConfig.disableDeclineAlways,
            disableAgreeFlush: this.data.gdprConfig && this.data.gdprConfig.disableAgreeFlush,
            onAgree: () => {
                loadScriptAsync([gdprUrl]);
            },
            onDecline: () => {
                if (!this.data.gdprConfig || !this.data.gdprConfig.disableDefaultDecline) {
                    this.goLogin();
                }
            },
        });
    }

    public initGdpr() {
        if (ua.isUMUWebview) {
            return;
        }
        if (ua.isInIframe()) {
            return;
        }
        const userId = getData(this.data, 'userInfo', 'student_id') || '';
        const lang = getData(this.data, 'Lang');


        gdpr({
            userId,
            platform: window.location.host.indexOf('m.') === 0 ? 'm' : 'web',
            lang,
        });
    }

    public setupFetchErrorListener() {
        window.addEventListener('message', (event) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            if (event.data.type !== 'fetch-alert-message') {
                return;
            }

            const { title, content, btnConfirmText, gotoLoginPageOnConfirmClicked } = event.data;
            if (!title || !content || !btnConfirmText) {
                return;
            }

            const submitBtnClick = gotoLoginPageOnConfirmClicked
                ? () => {
                      window.location.href = getLoginUrl();
                  }
                : () => {
                      /** */
                  };

            AlertDialog({
                title,
                content,
                btnConfirmText,
                submitBtnClick,
            });
        });
    }

    /**
     * 动态加载vconsole
     */
    public initVconsole() {
        if (/debugger=1/.test($window.location.search)) {
            loadScriptAsync(['https://statics-cdn-cn.umucdn.cn/common/vconsole.min.js?v=3.2.0']).then(() => {
                const VConsole = $window.VConsole;
                // tslint:disable-next-line: no-unused-expression
                VConsole && new VConsole();
            });
        }
    }

    public fisIOS12KeyboardBug() {
        // https://juejin.im/post/5c07442f51882528c4469769
        if (!ua.isIos) {
            return;
        }
        document.addEventListener(
            'blur',
            (e: any) => {
                if (['input', 'textarea'].indexOf(e.target.localName) !== -1) {
                    document.documentElement.scrollTop = document.documentElement.scrollTop;
                    document.body.scrollTop = document.body.scrollTop;
                }
            },
            true,
        );
    }

    public loadPingppSync() {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://statics-umu-cn.umucdn.cn/cdn/js/pay/pingpp.2.2.1.js';
        document.body.appendChild(script);
    }

    public disableMobileFixedLayerWhenKeyboardAppears() {
        // mobile设备上，有focus事件弹起键盘后，去除doc-fixed效果
        if (!ua.isMobile) {
            return;
        }
        let timer = 0;
        // 支持处理的元素父容器选择器列表
        const list = ['comment-reply-poster', 'comment-edit-poster', 'video-comment-main-poster'];
        const isMatchParent = (e: any) => {
            const path: HTMLElement[] = e.path || (e.composedPath && e.composedPath()) || [];
            const ret = path.some((el) => {
                return list.some((selector) => {
                    return (el.className || '').indexOf(selector) !== -1;
                });
            });
            return ret;
        };
        const handlerIn = (e: any) => {
            if (!isMatchParent(e)) {
                return;
            }
            const noDocFixedCls = 'no-doc-fixed-switch';
            if (document.body.className.indexOf(noDocFixedCls) === -1) {
                document.body.className += ' ' + noDocFixedCls;
            }
        };
        const handlerOut = () => {
            const noDocFixedRegexp = new RegExp('\\s' + 'no\\-doc\\-fixed\\-switch', 'g');
            document.body.className = document.body.className.replace(noDocFixedRegexp, '');
        };

        document.addEventListener(
            'focusin',
            (e: any) => {
                clearTimeout(timer);
                handlerIn(e);
            },
            false,
        );
        document.addEventListener(
            'focusout',
            () => {
                clearTimeout(timer);
                timer = $window.setTimeout(() => {
                    handlerOut();
                }, 300);
            },
            false,
        );
    }

    public checkXFrameAllow() {
        checkXFrameAllow();
    }

    public init() {
        const isSystemCN = isSystem('CN') === true;
        if (!isSystemCN) {
            if (isSystem('en')) {
                this.initGdprCom();
            } else {
                this.initGdpr();
            }
            imageApi.listen();
        }

        renderFullWatermark();
        this.setupFetchErrorListener();
        this.fisIOS12KeyboardBug();
        this.initVconsole();
        this.disableMobileFixedLayerWhenKeyboardAppears();
        isInMiniProgram((is) => {
            if (!is) {
                if (isSystemCN) {
                    this.loadPingppSync();
                }
            }
        });
        /**
         * @desc 解除弹窗微信限制
        */
        userAchievementService.startup();
        this.checkXFrameAllow();

        // 添加initBodyClassName，在body中注入css命名空间
        initBodyClassName();
    }

    public setupPageTitle(title: string) {
        setTitle(title);
    }
}

export default Page;
